import type { AiAgentSettingsData, OverviewData, ReviewItem } from "../types";
import { StarRating } from "../StarRating";
import { reviewMatchesFilter } from "../review-filters";
import { originalReviewText } from "../review-text";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return (name.slice(0, 2) || "CD").toUpperCase();
}

function formatDate(isoString: string): string {
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function formatAgo(isoString: string): string {
  try {
    const d = new Date(isoString);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return "Hoy";
    if (diffDays === 1) return "Hace 1 día";
    if (diffDays < 30) return `Hace ${diffDays} días`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return "Hace 1 mes";
    return `Hace ${diffMonths} meses`;
  } catch {
    return "Reciente";
  }
}

export function OverviewView({
  data,
  reviews,
  aiAgentSettings,
  userName,
  base,
  navigate,
}: {
  data: OverviewData;
  reviews: ReviewItem[];
  aiAgentSettings?: AiAgentSettingsData;
  userName: string;
  base: string;
  navigate: (path: string) => void;
}) {
  const kpi = data.kpi || {
    rating: 0,
    review_count: 0,
    new_reviews: 0,
    unanswered: 0,
    negative_month: 0,
    responded_month: 0,
  };
  // Match the actual Reviews tab, rather than historical/unactionable suggested drafts.
  const pendingApprovalCount = reviews.filter((review) => reviewMatchesFilter(review, "para_revisar")).length;
  const unansweredCount = reviews.filter((review) => reviewMatchesFilter(review, "sin_responder")).length;
  // Keep the negative CTA population identical to the actionable "Negativas" inbox filter.
  const negativeActionableCount = reviews.filter((review) => reviewMatchesFilter(review, "negativas")).length;
  const greeting = userName.trim() ? `Hola, ${userName.trim()}` : "Hola";
  const latestReview = reviews
    .slice()
    .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime())[0] || null;

  // Próxima acción recomendada (prioridad: negativas sin responder > borradores IA > sin responder > todo al día)
  let hero: {
    eyebrow: string;
    title: string;
    body: string;
    href: string;
    cta: string;
    calm: boolean;
  };

  if (negativeActionableCount > 0) {
    hero = {
      eyebrow: "Próxima acción recomendada",
      title: `Respondé ${negativeActionableCount} ${negativeActionableCount === 1 ? "reseña negativa pendiente" : "reseñas negativas pendientes"}`,
      body: "Priorizá las negativas: una respuesta rápida y empática puede evitar que escalen.",
      href: `${base}/reviews?tab=negativas`,
      cta: "Revisar ahora",
      calm: false,
    };
  } else if (pendingApprovalCount > 0) {
    hero = {
      eyebrow: "Próxima acción recomendada",
      title: `Revisá ${pendingApprovalCount} ${pendingApprovalCount === 1 ? "respuesta derivada" : "respuestas derivadas"}`,
      body: "Estos borradores requieren tu revisión. Desde Reseñas podés ajustar el texto y confirmar expresamente cada publicación en Google.",
      href: `${base}/reviews?tab=para_revisar`,
      cta: "Revisar derivadas",
      calm: false,
    };
  } else if (unansweredCount > 0) {
    hero = {
      eyebrow: "Próxima acción recomendada",
      title: `Respondé ${unansweredCount} ${kpi.unanswered === 1 ? "reseña sin responder" : "reseñas sin responder"}`,
      body: aiAgentSettings?.mode === "safe_auto"
        ? "Revisá la cola. Los casos seguros sólo pueden automatizarse si la autorización y la habilitación del servicio están vigentes."
        : "Prepará una respuesta con IA, revisala y publicala cuando esté lista.",
      href: `${base}/reviews?tab=sin_responder`,
      cta: "Ir a Reseñas",
      calm: false,
    };
  } else {
    hero = {
      eyebrow: "Todo al día",
      title: "No tenés pendientes urgentes",
      body: "Buen trabajo. Aprovechá para revisar tus Reportes o el estado de tu Agente IA.",
      href: `${base}/reports`,
      cta: "Ver reportes",
      calm: true,
    };
  }

  const isGoogleConnected = data.google?.status === "connected";
  const agentStatus = aiAgentSettings?.mode === "paused"
    ? {
      label: "Pausado",
      detail: "La automatización está pausada. Podés seguir probando y ajustando el agente.",
      tone: "warn",
    }
    : aiAgentSettings?.mode === "approval_required"
      ? {
        label: "Con aprobación",
        detail: "El agente redacta borradores y espera tu aprobación antes de publicar.",
        tone: "ok",
      }
      : aiAgentSettings?.mode === "safe_auto"
        ? {
          label: "Automático seguro",
          detail: "El agente evalúa tus reglas y deriva casos sensibles; publicar automáticamente también depende de la autorización y habilitación efectiva.",
          tone: "ok",
        }
        : {
          label: "Revisar",
          detail: "No pudimos confirmar el modo actual del agente.",
          tone: "warn",
        };

  return (
    <div className="rep-overview-view rep-layout-approved" data-layout="RB-INI-B0">
      <header className="page-heading">
        <p className="eyebrow">Centro de control</p>
        <h1>{greeting}</h1>
        <p>El pulso de tu reputación y las acciones recomendadas para tu negocio hoy.</p>
      </header>

      {/* Hero card: ONE primary action container */}
      <section className={`rep-hero-banner ${hero.calm ? "is-calm" : "is-priority"}`} aria-label={hero.eyebrow}>
        <div className="rep-hero-copy">
          <span className="rep-hero-eyebrow">{hero.eyebrow}</span>
          <h2>{hero.title}</h2>
          <p>{hero.body}</p>
          <a
            className="primary-button rep-hero-cta"
            href={hero.href}
            onClick={(e) => {
              e.preventDefault();
              navigate(hero.href);
            }}
          >
            {hero.cta} →
          </a>
        </div>

      </section>

      {/* Flat KPI / Pulse Strip */}
      <div className="rep-kpi-strip">
        <a
          className="rep-kpi-strip-item"
          href={`${base}/reviews?tab=todas`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`${base}/reviews`);
          }}
          aria-label="Ver todas las reseñas y sus calificaciones"
        >
          <span className="rep-kpi-strip-label">Rating actual</span>
          <strong className="rep-kpi-strip-value">{kpi.rating ? `${kpi.rating.toFixed(1)} ★` : "—"}</strong>
          <span className="rep-kpi-strip-sub">{kpi.review_count} reseñas acumuladas</span>
        </a>

        <div className="rep-kpi-strip-divider" aria-hidden="true" />

        <a
          className="rep-kpi-strip-item"
          href={`${base}/reviews`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`${base}/reviews`);
          }}
          aria-label="Ver reseñas del período"
        >
          <span className="rep-kpi-strip-label">Reseñas este mes</span>
          <strong className="rep-kpi-strip-value">{kpi.new_reviews}</strong>
          <span className="rep-kpi-strip-sub">{kpi.review_count} reseñas totales</span>
        </a>

        <div className="rep-kpi-strip-divider" aria-hidden="true" />

        <a
          className="rep-kpi-strip-item"
          href={`${base}/reviews?tab=sin_responder`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`${base}/reviews?tab=sin_responder`);
          }}
          aria-label="Ver reseñas sin responder"
        >
          <span className="rep-kpi-strip-label">Sin responder</span>
          <strong className={`rep-kpi-strip-value ${kpi.unanswered > 0 ? "highlight" : ""}`}>
            {kpi.unanswered}
          </strong>
          <span className="rep-kpi-strip-sub">
            {kpi.unanswered > 0 ? "Requieren respuesta" : "Al día"}
          </span>
        </a>
      </div>

      {/* Clean Operational Sections */}
      <div className="rep-split-grid">
        {/* Recent Activity Section */}
        <section className="rep-flat-section">
          <div className="rep-section-header">
            <div>
              <p className="eyebrow">Actividad reciente</p>
              <h2>Lo último en tu negocio</h2>
            </div>
            <a
              className="rep-link-action"
              href={`${base}/reviews`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`${base}/reviews`);
              }}
            >
              Ver todas →
            </a>
          </div>

          {latestReview ? (
            <div className="rep-recent-review-card">
              <div className="rep-review-author-row">
                <span className={`rep-author-avatar ${latestReview.rating <= 2 ? "danger" : ""}`}>
                  {initials(latestReview.author)}
                </span>
                <div>
                  <strong>{latestReview.author}</strong>
                  <div className="rep-author-meta">
                    <StarRating rating={latestReview.rating} size="sm" />
                    <span>Google · {formatAgo(latestReview.published_at)}</span>
                  </div>
                </div>
              </div>
              <p className="rep-review-snippet">
                {originalReviewText(latestReview.text) || "Reseña calificada sin comentario escrito."}
              </p>
              <a
                className="rep-inline-link"
                href={`${base}/reviews/${encodeURIComponent(latestReview.id)}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`${base}/reviews/${encodeURIComponent(latestReview.id)}`);
                }}
              >
                Ver detalle de reseña →
              </a>
            </div>
          ) : (
            <div className="empty-state">
              <p>Todavía no hay reseñas importadas.</p>
            </div>
          )}
        </section>

        {/* System Status Section */}
        <section className="rep-flat-section">
          <div className="rep-section-header">
            <div>
              <p className="eyebrow">Estado del sistema</p>
              <h2>Operación de reputación</h2>
            </div>
          </div>

          <div className="rep-system-list">
            <a
              className="rep-system-row"
              href={`${base}/settings/google`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`${base}/settings/google`);
              }}
            >
              <div className="rep-system-icon blue">G</div>
              <div className="rep-system-info">
                <strong>Google Business Profile</strong>
                <span>
                  {isGoogleConnected
                    ? "Conectado para sincronizar reseñas"
                    : "Conectá Google para sincronizar reseñas"}
                </span>
              </div>
              <span className={`rep-status-pill ${isGoogleConnected ? "ok" : "warn"}`}>
                <span className="rep-status-dot" aria-hidden="true" />
                {isGoogleConnected ? "Conectado" : "Revisar"}
              </span>
            </a>

            <a
              className="rep-system-row"
              href={`${base}/ai-agent`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`${base}/ai-agent`);
              }}
            >
              <div className="rep-system-icon orange rep-system-icon-agent" aria-hidden="true" />
              <div className="rep-system-info">
                <strong>Agente IA</strong>
                <span>{agentStatus.detail}</span>
              </div>
              <span className={`rep-status-pill ${agentStatus.tone}`}>
                <span className="rep-status-dot" aria-hidden="true" />
                {agentStatus.label}
              </span>
            </a>
          </div>

          <div className="rep-system-footer">
            <a
              className="rep-system-link-btn"
              href={`${base}/reports`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`${base}/reports`);
              }}
            >
              Ver reporte de rendimiento →
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
